import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Chip, IconButton, Paper, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { BoxedImage } from '../../../components/BoxedImage';
import { PogBBox, ProductRecognitionCropScanResponse } from '../../../types';
import { ImageZoomModal } from './ImageZoomModal';
import { ProductRecognitionViewer } from './ProductRecognitionViewer';

const ITEM_PREVIEW_SIZE = 120;

const getClassificationColor = (classificationType: string): string => {
  switch (classificationType) {
    case 'product':
      return '#4CAF50'; // Green for products
    case 'carton':
      return '#FF9800'; // Orange for cartons
    default:
      return '#2196F3'; // Blue for unknown/other types
  }
};

interface CropScanItemProps {
  cropScan: ProductRecognitionCropScanResponse & {
    isPending?: boolean;
    shelfColumnLabel?: string;
    pogPreview?: {
      pogBox: PogBBox;
    };
  };
  index: number;
  imageUrl: string;
  previewSize?: number;
  onCropScanClick?: (cropScan: ProductRecognitionCropScanResponse) => void;
  onCropViewerClick?: (cropScan: ProductRecognitionCropScanResponse) => void;
  onDelete?: (productCropScanId: number) => void;
  isSelected?: boolean;
}

export const CropScanItem: React.FC<CropScanItemProps> = ({
  cropScan,
  index,
  imageUrl,
  previewSize = ITEM_PREVIEW_SIZE,
  onCropScanClick,
  onCropViewerClick,
  onDelete,
  isSelected,
}) => {
  const [zoomState, setZoomState] = useState<{
    src: string;
    bbox?: [number, number, number, number];
  } | null>(null);
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);
  const [pogZoomSrc, setPogZoomSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!zoomState) {
      setLoadedImage(null);
      return;
    }
    const img = new Image();
    img.onload = () => setLoadedImage(img);
    img.src = zoomState.src;
  }, [zoomState]);

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          p: 1,
          cursor: onCropScanClick ? 'pointer' : 'default',
          backgroundColor: isSelected ? '#FFF3CD' : '#FFFFFF',
          border: isSelected ? '2px solid #FFC107' : '2px solid transparent',
          '&:hover': onCropScanClick
            ? {
                backgroundColor: isSelected ? '#FFE69C' : 'action.hover',
              }
            : {},
        }}
        onClick={() => onCropScanClick?.(cropScan)}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {/* Crop + POG Images */}
          <Box
            sx={{
              minWidth: '70px',
              alignSelf: 'flex-start',
              display: 'flex',
              alignItems: 'center',

              flexDirection: 'row',
              gap: 2,
            }}
          >
            {/* Camera crop */}
            <ProductRecognitionViewer
              imageUrl={imageUrl}
              cropScans={[cropScan]}
              mode="crop"
              focusCropScan={cropScan}
              width={previewSize}
              height={previewSize}
              onClick={() => {
                if (onCropViewerClick) {
                  onCropViewerClick(cropScan);
                } else {
                  setZoomState({
                    src: imageUrl,
                    bbox: cropScan.bbox as [number, number, number, number],
                  });
                }
              }}
              sx={{ cursor: 'zoom-in' }}
              hidePolygons
            />
            {/* POG item image - only when we have a matching POG box */}
            {cropScan.pogPreview &&
              cropScan.pogPreview.pogBox.imageUri &&
              !cropScan.pogPreview.pogBox.hasFallbackImage && (
                <>
                  &rarr;
                  <img
                    src={cropScan.pogPreview.pogBox.imageUri}
                    alt="POG item"
                    style={{
                      maxWidth: previewSize,
                      maxHeight: previewSize,
                      objectFit: 'contain',
                      borderRadius: 4,
                      border: '1px solid',
                      borderColor: 'rgba(0, 0, 0, 0.12)',
                      cursor: 'zoom-in',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPogZoomSrc(cropScan.pogPreview!.pogBox.imageUri!);
                    }}
                  />
                </>
              )}
          </Box>

          {/* Crop Info */}
          <Stack spacing={1} sx={{ flex: 1, width: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle2" sx={{ fontSize: '12px' }}>
                # {index + 1}
              </Typography>
              <Chip
                label={cropScan.classification_type}
                size="small"
                sx={{
                  backgroundColor: getClassificationColor(cropScan.classification_type),
                  color: 'white',
                  fontWeight: 500,
                  textTransform: 'capitalize',
                  fontSize: '12px',
                }}
              />
              {cropScan.shelfColumnLabel && (
                <Chip
                  label={cropScan.shelfColumnLabel}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    borderColor: '#1976d2',
                    color: '#1976d2',
                  }}
                />
              )}
              {cropScan.isPending && (
                <Chip label="Pending" color="warning" size="small" sx={{ fontSize: '12px' }} />
              )}
              {!cropScan.isPending && cropScan.barcode && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Complete"
                  color="success"
                  size="small"
                  sx={{ fontSize: '12px' }}
                />
              )}
            </Stack>

            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: '12px',
                  textAlign: 'left',
                }}
              >
                {cropScan.barcode
                  ? `${cropScan.barcode} • ${cropScan.product_name}${
                      cropScan.product_id ? ` • Product ID: ${cropScan.product_id}` : ''
                    }`
                  : '\u00A0'}
              </Typography>
            </Box>
          </Stack>

          {/* Delete Button */}
          {cropScan.barcode && cropScan.isPending && onDelete && (
            <div>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(cropScan.product_crop_scan_id);
                }}
                sx={{
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'error.contrastText',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          )}
        </Stack>
      </Paper>
      <ImageZoomModal
        isOpen={!!pogZoomSrc}
        imageSrc={pogZoomSrc ?? ''}
        onClose={() => setPogZoomSrc(null)}
      />
      {zoomState && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex justify-center z-50 p-8 overflow-y-auto"
          onClick={() => setZoomState(null)}
        >
          <div className="relative max-w-6xl m-auto" onClick={(e) => e.stopPropagation()}>
            {loadedImage ? (
              <BoxedImage image={loadedImage} crop={zoomState.bbox} cropPadding={20} boxes={[]} />
            ) : (
              <div className="text-white">Loading...</div>
            )}
            <button
              onClick={() => setZoomState(null)}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};
